import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { IndicatorChartTypeEnums } from 'src/app/enum/indicator-chart-enums';
import { SuspensionStateEnums } from 'src/app/enum/suspension-state-enum';
import { IndicatorChart } from 'src/app/interfaces/custom-indicators/indicator-chart.interface';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ItemChartModel } from 'src/app/models/charts/global/item-chart.model';
import { SuspensionChartModel } from 'src/app/models/charts/rental/suspension-chart.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { SuspensionDetailTableModel } from 'src/app/models/tables/rental/suspension-detail-table.model';
import { SuspensionTableModel } from 'src/app/models/tables/rental/suspension-table.model';
import { SuspensionDetailService } from 'src/app/services/rental/suspension-detail.service';
import { SuspensionService } from 'src/app/services/rental/suspension.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InvoiceSuspensionTableModel } from 'src/app/models/tables/rental/invoice-suspension-table.model';

const SuspensionIndicatorsConstants = {
  states: [
    { id: SuspensionStateEnums.open, label: 'Abiertas', color: '#EF4444' },
    { id: SuspensionStateEnums.printed, label: 'Impresas', color: '#3B82F6' },
    { id: SuspensionStateEnums.closed, label: 'Cerradas', color: '#198754' },
    { id: SuspensionStateEnums.annul, label: 'Anuladas', color: '#FB923C' }
  ]
};

@Component({
  selector: 'app-suspension',
  templateUrl: './suspension.component.html',
  styleUrls: ['./suspension.component.scss'],
  standalone: false
})
export class SuspensionComponent implements OnInit {

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<SuspensionDetailTableModel> = new PagedResponseModel<SuspensionDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = []
  generalData: PagedResponseModel<SuspensionTableModel> = new PagedResponseModel<SuspensionTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  indicatorCharts: IndicatorChart[] = this.buildIndicatorCharts(new SuspensionChartModel());
  indicatorsRequestId: number = 0;
  invoiceData: PagedResponseModel<InvoiceSuspensionTableModel> = new PagedResponseModel<InvoiceSuspensionTableModel>();
  invoiceHeader: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined
  limit: number = 10;
  loadingIndicators: boolean = false;
  nameWindow: string = 'Suspensiones';
  page: number = 1;
  previousSort: { field: string; isAscsendant: number };
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];
  url: string = '';

  constructor(private suspensionService: SuspensionService,
    private suspensionDetailService: SuspensionDetailService,
    private router: Router,
    private headerService: HeaderService,
    private title: Title) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem('suspension');

    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllSuspensionsWithPagination(),
      this.getIndicatorsForGeneralConsultation()
    ]);
  }

  buildIndicatorCharts(indicators: SuspensionChartModel): IndicatorChart[] {
    const { states } = SuspensionIndicatorsConstants;
    return [
      {
        type: IndicatorChartTypeEnums.donut,
        title: 'Suspensiones por estado',
        labels: states.map(state => state.label),
        values: states.map(state => this.getIndicatorQuantity(indicators.states, state.id)),
        colors: states.map(state => state.color),
        total: indicators.total
      }
    ];
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(this.suspensionDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Suspension Detalles.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error while exporting suspension details.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(this.suspensionService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Suspensiones.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error while exporting suspensions.', error);
    }
  }

  async exportInvoiceForSuspensionConsultation() {
    try {
      const blob = await firstValueFrom(this.suspensionService.exportInvoiceForSuspensionConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Suspensión Facturas.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las facturas de suspensiones detalle.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.suspensionDetailService.getColumnsForDetailConsultation());
      }
    } catch (error) {
      console.error('Error to getting the columns for the detail query.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.suspensionService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error to getting the columns for the general query.', error);
    }
  }

  async getAllInvoiceForSuspensionsWithPagination() {
    try {
      this.invoiceData = await firstValueFrom(this.suspensionService.getAllInvoiceForSuspensionsWithPagination(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters)
      );
    } catch (error) {
      console.error('Error al buscar las facturas para las suspensiones.', error);
    }
  }

  async getAllSuspensionsWithPagination() {
    try {
      this.generalData = await firstValueFrom(this.suspensionService.getAllSuspensionsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error while getting the suspension.', error);
    }
  }

  async getAllSuspensionDetailsWithPagination() {
    try {
      this.detailData = await firstValueFrom(this.suspensionDetailService.getAllSuspensionDetailsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error while getting the suspension details.', error);
    }
  }

  async getColumnsForInvoiceConsultation() {
    try {
      if (this.invoiceHeader.length === 0) {
        this.invoiceHeader = await firstValueFrom(this.suspensionService.getColumnsForInvoiceConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta de facturas.', error);
    }
  }

  async getIndicatorsForGeneralConsultation() {
    // Descarta respuestas de solicitudes anteriores que lleguen después de la última
    const requestId = ++this.indicatorsRequestId;
    this.loadingIndicators = true;
    try {
      const indicators = await firstValueFrom(this.suspensionService.getIndicatorsForGeneralConsultation(this.filters));
      if (requestId === this.indicatorsRequestId) {
        this.indicatorCharts = this.buildIndicatorCharts(indicators);
      }
    } catch (error) {
      console.error('Error al buscar los indicadores de las suspensiones.', error);
    } finally {
      if (requestId === this.indicatorsRequestId)
        this.loadingIndicators = false;
    }
  }

  getIndicatorQuantity(items: ItemChartModel[], id: number): number {
    return items?.find(item => item.id === id)?.quantity ?? 0;
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;
    this.invoiceHeader.length = this.invoiceHeader.length === 1 ? 0 : this.invoiceHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getAllSuspensionsWithPagination(),
          this.getIndicatorsForGeneralConsultation()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getAllSuspensionDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 2:
        await Promise.all([
          this.getColumnsForInvoiceConsultation(),
          this.getAllInvoiceForSuspensionsWithPagination()
        ]);
        this.invoiceData = {
          ...this.invoiceData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  newSuspension() {
    this.router.navigate(['/suspension/register']);
  }

  onAction(event: { action: string, item?: SuspensionTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/suspension/register/${event.item.suspensionId}`]);
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
      case 2:
        await this.exportInvoiceForSuspensionConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getAllSuspensionsWithPagination()
        break;
      case 1:
        await this.getAllSuspensionDetailsWithPagination()
        break;
      case 2:
        await this.getAllInvoiceForSuspensionsWithPagination()
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}